import type { ReactNode } from "react";

import type { Mark } from "@/lib/attributeReview/stepMarks";
import { cn } from "@/lib/utils";

const BADGE: Record<Mark, string> = {
  done: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  warn: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  err: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  todo: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};
const STATUS: Record<Mark, string> = {
  done: "text-emerald-600 dark:text-emerald-300",
  warn: "text-amber-700 dark:text-amber-300",
  err: "text-rose-700 dark:text-rose-300",
  todo: "text-slate-600 dark:text-slate-300",
};
const GLYPH: Partial<Record<Mark, string>> = { done: "✓", warn: "!", err: "✕" };

type Props = {
  n: number;
  mark: Mark;
  title: string;
  status: string;
  last?: boolean;
  children: ReactNode;
};

export function ReviewStep({
  n,
  mark,
  title,
  status,
  last = false,
  children,
}: Props) {
  return (
    <section
      className="grid grid-cols-[30px_minmax(0,1fr)] gap-3.5"
      aria-label={`Step ${n}: ${title}`}
    >
      <div className="flex flex-col items-center gap-1.5">
        <span
          aria-hidden
          className={cn(
            "flex h-[30px] w-[30px] items-center justify-center rounded-full text-[13px] font-bold",
            BADGE[mark],
          )}
        >
          {GLYPH[mark] ?? n}
        </span>
        {!last && (
          <span
            aria-hidden
            className="w-0.5 flex-1 rounded bg-slate-200 dark:bg-slate-800"
          />
        )}
      </div>
      <div
        className={cn("flex min-w-0 flex-col gap-2.5", !last && "pb-[18px]")}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-body text-sm font-semibold text-slate-900 dark:text-slate-50">
            {title}
          </h3>
          <span className={cn("text-xs font-medium", STATUS[mark])}>
            {status}
          </span>
        </div>
        {children}
      </div>
    </section>
  );
}
